import _ from 'lodash'
import apis from 'browser/app/models/apis'
import { Entity } from 'shared-libs/models/entity'
import { getStoryboardExecution } from 'shared-libs/models/types/storyboard/storyboard-utils'
import { PlatformType } from 'shared-libs/models/types/storyboard/storyboard-plan'
import { ErrorModal } from 'browser/components/atomic-elements/organisms/error-modal'
import { browserHistory } from 'browser/history'
import queryString from 'query-string'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Classes } from '@blueprintjs/core'

import { ImageLoader } from 'browser/components/atomic-elements/atoms/image-loader/image-loader'
import { Icon } from './icon'

import './_kiosk.scss'

/**
 * Lightweight UI components and helper functions for the kiosk
 */
const vectorIcon = require('images/navigation/vector-logo-black.svg')

// ui components
export const KioskNextButton: React.FC<any> = ({ isDisabled, isLoading, onClick, children }) => {
  return (
    <div className="kiosk-action-footer">
      <Button
        className={classNames(Classes.INTENT_PRIMARY, Classes.FILL, 'kiosk-next-button')}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onClick={onClick}
      >
        {children}
      </Button>
    </div>
  )
}

export const KioskCompanyLogo: React.FC<any> = () => {
  return (
    <div className="kiosk-company-logo-container">
      <ImageLoader className="kiosk-company-logo" src={getLogoUri()} />
    </div>
  )
}

export const KioskInputField: React.FC<any> = (props) => {
  const [internalValue, setInternalValue] = useState(props.value)
  const { id, label, onChange, value, iconName, iconSize = '20' } = props

  const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInternalValue(newValue)

    if (value !== newValue) {
      onChange?.(newValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Highlight all text so the next scan will replace it all
      e.currentTarget.select()
    }

    props.onKeyDown?.(e)
  }

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  return (
    <div className="kiosk-input-field" style={props.style}>
      {iconName && (
        <Icon className="kiosk-input-field__left-icon" name={iconName} size={iconSize} />
      )}
      <div className="kiosk-input-field__group">
        {!_.isEmpty(value) && <label htmlFor={id + '_label'}>{label}</label>}
        <input
          {...props}
          readOnly={_.isNil(onChange)}
          id={id + '_input'}
          name={id}
          type="text"
          value={internalValue}
          onChange={handleOnInput}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  )
}

export function getLogoUri(): string {
  const settings = apis.getSettings()
  const firm = settings.getFirm()
  const kioskSettings = settings.getKioskSettings()
  const firmLogo = _.get(firm, 'business.logo.uri')

  return _.get(kioskSettings, 'core_settings_kiosk.logo.uri') ?? firmLogo ?? vectorIcon
}

export function getKioskSettingsValue(name: string, defaultValue: string) {
  const kioskSettings = apis.getSettings().getKioskSettings()
  return _.get(kioskSettings, 'core_settings_kiosk.' + name, defaultValue)
}

export async function navigateToKioskStory(entity: Entity, storyId: string | null = null) {
  const executionEntity = getStoryboardExecution(entity)
  executionEntity.initializeOtherContents()
  await executionEntity.hydrate()

  const kioskStory = _.first(executionEntity.getStoriesForCurrentUser(PlatformType.KIOSK))

  if (_.isEmpty(kioskStory)) {
    const message = getKioskSettingsValue('noKioskStory', 'This plan does not have kiosk story')

    ErrorModal.open({
      errorTitle: 'No kiosk story',
      errorText: message,
    })
    return
  }

  const searchParams = {
    story_id: storyId ?? kioskStory?.id,
  }

  browserHistory.push({
    pathname: `/entity/${entity.uniqueId}`,
    search: queryString.stringify(searchParams),
  })
}
